<ng-container *ngIf="!error; else errorState">
  <mat-table
    [dataSource]="dataSource"
    [trackBy]="getProjectId"
    matSort
    matSortActive="label"
    matSortStart="asc"
    matSortDisableClear="true"
    [formGroup]="filters">

    <ng-container matColumnDef="progress">
      <mat-cell *matCellDef="let element"/>
      <mat-header-cell *matHeaderCellDef [attr.colspan]="projectColumns.length">
        @if (projectQuery.isFetching()) {
          <fw-progress-bar/>
        }
      </mat-header-cell>
    </ng-container>

    <ng-container matColumnDef="lock">
      <mat-header-cell *matHeaderCellDef></mat-header-cell>
      <mat-cell *matCellDef="let element">
        <project-lock-status [project]="element"></project-lock-status>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="label">
      <mat-header-cell *matHeaderCellDef mat-sort-header class="full-width">
        <!-- keydown and click listeners are to stop the sort header from stealing the event -->
        <fw-text-input test-id="project-label-filter" formControlName="project" (keydown)="$event.stopPropagation()" (click)="$event.stopPropagation()"
          placeholder="Project" size="small"></fw-text-input>
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a test-id="project-label" href="#/projects/{{ project._id }}/description" [fwTooltip]="project.label"
          fwTooltipPosition="above" class="ellipsis">
          {{ project.label }}
        </a>
        <ng-container *featureIf="['ohif_react']">
          <mat-icon
            *permissionIf="'files_view_contents'; project: project._id"
            (click)="viewProjectStudies(project)"
            class="load-ohif xs"
            fwTooltip="Viewer Study List"
            fwTooltipPosition="right">
            panorama
          </mat-icon>
        </ng-container>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="group">
      <mat-header-cell *matHeaderCellDef mat-sort-header class="full-width">
        <!-- keydown and click listeners are to stop the sort header from stealing the event -->
        <fw-text-input test-id="group-label-filter" formControlName="group" (keydown)="$event.stopPropagation()" (click)="$event.stopPropagation()"
          placeholder="Group" size="small"></fw-text-input>
      </mat-header-cell>
      <mat-cell test-id="group-label" *matCellDef="let project">
        <span class="ellipsis" [fwTooltip]="project?.group_info?.label" fwTooltipPosition="above">
          {{ project?.group_info?.label || project.group }}
        </span>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="subject_count">
      <mat-header-cell *matHeaderCellDef mat-sort-header>
        Subjects
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a test-id="subject-count" href="#/projects/{{ project._id }}/subjects"
          [ngClass]="{'muted-text': !project.stats?.number_of.subjects}">{{ project.stats?.number_of.subjects || 0 }}</a>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="session_count">
      <mat-header-cell *matHeaderCellDef mat-sort-header>
        Sessions
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a test-id="session-count" href="#/projects/{{ project._id }}/sessions"
          [ngClass]="{'muted-text': !project.stats?.number_of.sessions}">
          {{ project.stats?.number_of.sessions || 0 }}
          <mat-icon *ngIf="features.sessionTemplatesEnabled && project.stats?.number_of.noncompliant_sessions"
            [fwTooltip]="getFlaggedTooltip(project)" fwTooltipPosition="above" class="flag xs">flag</mat-icon>
        </a>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="roles">
      <mat-header-cell *matHeaderCellDef>
        <fw-multi-select
          [formControl]="filters.controls.roles"
          titleProperty="label"
          valueProperty="_id"
          showFilter="false"
          [showClear]="true"
          [filterItemsOnSelect]="false"
          [useCheckbox]="true"
          [maxSelectedShown]="0"
          [options]="projectRolesQuery.data()?.results || []"
          optionsWidth="320px"
          placeholder="Roles"
          size="small"
          maxOptionsHeight="400px"
          width="100px"
        ></fw-multi-select>
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <a
          [fwTooltip]="getUsersRoleLabel(project)"
          test-id="project-roles"
          class="ellipsis"
          href="#/projects/{{ project._id }}/permissions">
          {{ getUsersRoleLabel(project) }}
        </a>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="users">
      <mat-header-cell *matHeaderCellDef>
        <fw-multi-select
          size="small"
          placeholder="Users"
          [showClear]="true"
          optionsWidth="fit-content"
          [maxSelectedShown]="0"
          [filterItemsOnSelect]="false"
          [formControl]="filters.controls.users"
          maxOptionsHeight="300px">
          <fw-menu-item
            *ngFor="let user of userOptions()"
            [value]="user._id"
            [title]="user.firstname + ' ' + user.lastname">
            <fw-avatar
              content="initial"
              size="medium"
              [appUser]="user"
            />
          </fw-menu-item>
        </fw-multi-select>
      </mat-header-cell>
      <mat-cell *matCellDef="let project">
        <fw-avatar-list [routerLink]="[project._id, 'permissions']">
        @for (user of project.permissions; track user._id) {
          <fw-avatar
            content="initial"
            size="medium"
            [appUser]="user"
            [appUserTooltip]="true"
          />
        }
        @empty {
          <span class="muted-text" ngProjectAs="fw-avatar">No Users</span>
        }
        </fw-avatar-list>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="actions">
      <mat-header-cell *matHeaderCellDef>
        <button
          test-id="new-project-button"
          mat-flat-button color="primary"
          (click)="newProject()">
          New Project
        </button>
      </mat-header-cell>
      <mat-cell *matCellDef="let project">

        <app-ldap-indicator *featureIf="['ldap_sync']" [ldapStatus]="project.ldap_sync"/>

        <div class="list-action-buttons" fwMenuCloseTriggers>
          <fw-icon-button
            [cdkMenuTriggerFor]="actionMenu"
            fwMenuRegister
            icon="menu-vertical"
            fwTooltip="Project Actions"
          />
          <ng-template #actionMenu>
            <fw-menu-container>
              <fw-menu>
                @for (menuItem of actionMenuItems; track menuItem.label) {
                  <ng-container *featureIf="menuItem.features; type: 'any'">
                    <a [routerLink]="[project._id, menuItem.href]">
                      <fw-menu-item
                        *permissionIf="menuItem.permissions; project: project._id; type: 'any'"
                        [title]="menuItem.label"
                      />
                    </a>
                  </ng-container>
                }
              </fw-menu>
            </fw-menu-container>
          </ng-template>
        </div>
      </mat-cell>
    </ng-container>

    <div *matNoDataRow class="empty-state" test-id="empty-state">
      @if(!projectQuery.isLoading()) {
        @if (filtersActive()) {
          <fw-layout-context
            title="No Projects Found"
            icon="search-loop"
            description="Adjust your search or filter to find existing projects or click `Create New Project` to get started">
            <fw-button (click)="filters.reset()" variant="outline">Clear Filters</fw-button>
            <fw-button (click)="newProject()" leftIcon="add-new-create">New Project</fw-button>
          </fw-layout-context>
        }
        @else {
          <fw-layout-context
            title="No Projects Exist"
            icon="list-paragraph"
            description="It looks like you don't have access to any projects yet. If you'd like to create one, click `Create New Project` to get started!">
            <fw-button (click)="newProject()">Create New Project</fw-button>
          </fw-layout-context>
        }
      }
    </div>

    <mat-header-row *matHeaderRowDef="projectColumns; sticky: true"/>
    <!-- Extra header row for displaying the loading bar -->
    <mat-header-row class="loading-row" *matHeaderRowDef="['progress']; sticky: true"/>

    <mat-row *matRowDef="let row; columns: projectColumns;" class="list-action-hover"></mat-row>
  </mat-table>




  <mat-paginator [pageIndex]="page() - 1" (page)="onPageChange($event)" [length]="projectQuery.data()?.total || 0" [pageSize]="pageSize" [hidePageSize]="true"></mat-paginator>
</ng-container>

<ng-template #loadingState>
  <div test-id="loading-state" class="blank-state">
    <img src="/assets/images/loading.svg" style="margin-bottom:10px">
    <p>Loading Projects</p>
  </div>
</ng-template>

<ng-template #errorState>
  <div test-id="error-state" class="blank-state">
    <mat-icon>error_outline</mat-icon>
    <p>Projects could not be retrieved.</p>
    <a test-id="reload-projects-button" class="btn btn-primary"
      >Reload Projects</a>
  </div>
</ng-template>

<ng-template #noAccess>
  <em class="muted-text">No Access</em>
</ng-template>
